/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: TimeGroupsPage.tsx
 ??? Created: 2025-01-15
 ?? Description:
    Time Groups list page with table view, search, pagination, and actions.
    Allows viewing, creating, editing, and deleting time groups.

 ?? Notes:
    - Domain-scoped (multi-tenant safe)
    - Supports search and pagination
    - Shows schedule count for each group

 ?? Last Updated: 2025-11-07 by GPT-5 Codex
----------------------------------------------------------------*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Copy, Edit, Trash2 } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import DataGridSimple from '../../components/ui/DataGridSimple';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deleteTimeGroupById, navigateToTimeGroupDuplicate } from './timeGroupsActions';
import { useTimeGroupList } from './hooks/useTimeGroupList';
import { TimeGroup } from './types';

const ACTION_BUTTON_CLASSNAMES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
};

export default function TimeGroupsPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const pageTitle = useMemo(() => t('timeGroups.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<TimeGroup | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useTimeGroupList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'name') {
          return {
            ...column,
            render: (_value: unknown, item: TimeGroup) => (
              <span className="font-medium text-foreground">{item.name}</span>
            ),
          };
        }
        if (column.key === 'description') {
          return {
            ...column,
            render: (_value: unknown, item: TimeGroup) => (
              <span className="text-muted-foreground max-w-md truncate block">
                {item.description || '-'}
              </span>
            ),
          };
        }
        if (column.key === 'schedules') {
          return {
            ...column,
            render: (_value: unknown, item: TimeGroup) => (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                {item._count?.schedules || 0} {t('timeGroups.scheduleCount')}
              </span>
            ),
          };
        }
        if (column.key === 'enabled') {
          return {
            ...column,
            render: (_value: unknown, item: TimeGroup) =>
              item.enabled ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  {t('common:enabled')}
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
                  {t('common:disabled')}
                </span>
              ),
          };
        }
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: TimeGroup) => {
              const date = new Date(item.createdAt);
              const isValidDate = !isNaN(date.getTime());
              return (
                <span className="text-sm text-muted-foreground">
                  {isValidDate ? date.toLocaleDateString() : t('common:notAvailable')}
                </span>
              );
            },
          };
        }
        return column;
      }),
    [baseColumns, t],
  );

  const handleCreate = () => {
    navigate('/pbx/incoming-tools/time-groups/new');
  };

  const handleEdit = (group: TimeGroup) => {
    navigate(`/pbx/incoming-tools/time-groups/${group.uuid ?? group.id}`);
  };

  const handleDuplicate = (group: TimeGroup) => {
    navigateToTimeGroupDuplicate(navigate, group.uuid ?? group.id);
  };

  const handleDelete = useCallback((group: TimeGroup) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!groupToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTimeGroupById(groupToDelete.id);
      showSuccess(t('timeGroups.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setGroupToDelete(null);
      refresh();
    } catch (error) {
      showError(error.message || t('timeGroups.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [groupToDelete, showError, showSuccess, t, refresh]);

  const renderActions = useCallback(
    (group: TimeGroup) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common:edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(group)}
            aria-label={t('common:edit')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.edit}`}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(group)}
            aria-label={t('common:duplicate')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.duplicate}`}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:delete')} position="top">
          <button
            type="button"
            onClick={() => handleDelete(group)}
            aria-label={t('common:delete')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.delete}`}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDelete, handleDuplicate, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            titleIcon={<Clock className="h-5 w-5 text-primary" />}
            columns={columns}
        onColumnToggle={handleColumnToggle}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        searchQuery={searchQuery}
        onSearch={handleSearch}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onAdd={handleCreate}
        title={t('timeGroups.title')}
        addButtonText={t('timeGroups.actions.create')}
        searchPlaceholder={t('timeGroups.searchPlaceholder')}
        renderActions={renderActions}
      />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title={t('timeGroups.messages.confirmDeleteTitle')}
            message={t('timeGroups.messages.confirmDeleteMessage', {
              name: groupToDelete?.name ?? '',
            })}
            confirmText={t('common:delete')}
            cancelText={t('common:cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}

